import { inject, injectable } from 'inversify';
import ErrorCodeHelper from '@dealroadshow/error-codes';
import Dispatcher, { RpcError } from '@dealroadshow/json-rpc-dispatcher';
import Logger from '@framework/sentry/Logger';
import { IMessageData, TMessageCode, TMessageName } from '../interfaces';
import { type IMessageStrategy } from './Strategy';
import { MessageStrategyTypes } from './DiTypes';

@injectable()
class MessageManager {
  constructor(@inject(MessageStrategyTypes) private messageStrategy: IMessageStrategy) {}

  /**
   * Get message from code with rendered data
   */
  getMessageByName(name: TMessageName, data?: IMessageData) {
    return this.messageStrategy.getMessageByName(name, data);
  }

  /**
   * Get fallback message
   * This message is used when no other message is found or when the error is not recognized
   */
  private getFallbackMessage(data?: IMessageData) {
    const fallbackMessageName = this.messageStrategy.getFallbackMessageName();

    return this.getMessageByName(fallbackMessageName, data);
  }

  /**
   * Get message by error code
   */
  getMessageByErrorCode(code: TMessageCode, data?: IMessageData) {
    const name = this.messageStrategy.getMessageNameByErrorCode(code);

    return this.getMessageByName(name, data);
  }

  /**
   * Get message from json rpc error
   */
  getErrorMessage(error: unknown, data?: IMessageData): string {
    if (Dispatcher.isBrowserAbortError(error)) {
      return this.getFallbackMessage();
    }

    if (!(error instanceof RpcError)) {
      Logger.error({ message: 'Error from getErrorMessage, but not RpcError.', error });
      return this.getFallbackMessage();
    }

    const fullData = { ...error.getData(), ...data };
    const errorCode = error.getCode();

    if (errorCode === ErrorCodeHelper.getCodeByName('WRAPPED_ERROR') && error.getData().code) {
      const unwrappedError = new RpcError({
        id: error.getId(),
        error: {
          ...error.getData(),
        },
      });

      return this.getErrorMessage(unwrappedError, data);
    }

// @ts-ignore TODO: FIN-32798 strictNullChecks
    return this.getMessageByErrorCode(errorCode, fullData);
  }
}

export default MessageManager;
